
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { ArrowLeft, Heart, Users, MapPin, Target } from 'lucide-react';
import bubacarrImage from '@/assets/bubacarr-manneh.jpeg';

const AboutUsPage = () => {
  const leadership = [
    {
      name: "Omar Manneh",
      role: "Co-Founder & CEO",
      bio: "Visionary leader driving GAMBGO's mission to transform food delivery in Gambia.",
      image: "/lovable-uploads/e8b4020f-aa0b-480a-b589-25e4e4db320b.png"
    },
    {
      name: "Bubacarr Manneh",
      role: "Co-Founder & CFO",
      bio: "Financial strategist ensuring sustainable growth and operational excellence.",
      image: bubacarrImage
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 px-4 py-4 animate-fade-in">
        <div className="max-w-6xl mx-auto flex items-center gap-4">
          <Link to="/">
            <Button variant="ghost" size="sm" className="text-gray-700 hover:scale-105 transition-transform duration-200">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          </Link>
          <div className="flex items-center gap-3">
            <img 
              src="/lovable-uploads/917e51ff-8660-4c82-b344-b93189a34ba9.png" 
              alt="GAMBGO Logo"
              className="w-8 h-8"
            />
            <span className="text-lg font-bold text-gray-900">GAMBGO</span>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-16 px-4 bg-gradient-to-br from-red-50 to-white">
        <div className="max-w-4xl mx-auto text-center animate-fade-in">
          <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6 animate-scale-in">
            About GAMBGO
          </h1>
          <p className="text-xl text-gray-700 leading-relaxed animate-fade-in" style={{ animationDelay: '0.2s' }}>
            We're on a mission to transform how Gambians access food, groceries, and essential services through innovative technology and exceptional delivery experiences.
          </p>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="animate-fade-in">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center hover:scale-110 transition-transform duration-200">
                  <Target className="w-6 h-6 text-red-600" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900">Our Mission</h2>
              </div>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                To connect every Gambian with their favorite local restaurants, grocery stores, and essential services through a seamless digital platform that delivers happiness to their doorstep.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                We believe in supporting local businesses, creating employment opportunities, and making daily life more convenient for our fellow Gambians.
              </p>
            </div>
            <div className="flex justify-center animate-fade-in" style={{ animationDelay: '0.3s' }}>
              <div className="w-80 h-80 bg-gradient-to-br from-red-100 to-red-200 rounded-full flex items-center justify-center hover:scale-105 transition-transform duration-300">
                <div className="text-6xl">🚀</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16 animate-fade-in">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Our Values
            </h2>
            <p className="text-xl text-gray-600">
              The principles that guide everything we do
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center bg-white rounded-xl p-8 shadow-sm hover:shadow-lg hover:scale-105 transition-all duration-300 animate-fade-in">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6 hover:bg-red-200 transition-colors duration-200">
                <Heart className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Community First</h3>
              <p className="text-gray-700 leading-relaxed">
                We prioritize the needs of our local community, supporting Gambian businesses and creating opportunities for growth.
              </p>
            </div>
            <div className="text-center bg-white rounded-xl p-8 shadow-sm hover:shadow-lg hover:scale-105 transition-all duration-300 animate-fade-in" style={{ animationDelay: '0.1s' }}>
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6 hover:bg-blue-200 transition-colors duration-200">
                <Users className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Excellence</h3>
              <p className="text-gray-700 leading-relaxed">
                We strive for excellence in every interaction, from the quality of food to the speed of delivery and customer service.
              </p>
            </div>
            <div className="text-center bg-white rounded-xl p-8 shadow-sm hover:shadow-lg hover:scale-105 transition-all duration-300 animate-fade-in" style={{ animationDelay: '0.2s' }}>
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 hover:bg-green-200 transition-colors duration-200">
                <MapPin className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Local Impact</h3>
              <p className="text-gray-700 leading-relaxed">
                Every order placed through GAMBGO directly supports local restaurants, vendors, and delivery partners across Gambia.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Leadership Team */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16 animate-fade-in">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Leadership Team
            </h2>
            <p className="text-xl text-gray-600">
              Meet the passionate individuals driving GAMBGO forward
            </p>
          </div>
          
          <div className="flex justify-center gap-16">
            {leadership.map((leader, index) => (
              <div key={index} className="text-center group animate-fade-in" style={{ animationDelay: `${index * 0.2}s` }}>
                <div className="relative mb-6">
                  <img 
                    src={leader.image}
                    alt={leader.name}
                    className="w-32 h-32 rounded-full mx-auto object-cover shadow-lg group-hover:shadow-xl group-hover:scale-110 transition-all duration-300"
                    style={leader.name === "Bubacarr Manneh" ? { objectPosition: 'center 20%' } : {}}
                  />
                  <div className="absolute inset-0 bg-red-600 rounded-full opacity-0 group-hover:opacity-10 transition-opacity duration-300"></div>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{leader.name}</h3>
                <p className="text-red-600 font-medium mb-3">{leader.role}</p>
                <p className="text-gray-600 text-sm leading-relaxed max-w-xs">{leader.bio}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 px-4 bg-red-600 text-white animate-fade-in">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-6">
            Ready to Join the GAMBGO Family?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Download the app today and experience the future of food and grocery delivery in Gambia.
          </p>
          <Link to="/install-guide">
            <Button 
              size="lg" 
              className="bg-white text-red-600 hover:bg-gray-100 hover:scale-105 px-8 py-3 text-lg font-semibold rounded-xl transition-all duration-200"
            >
              Get Started
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="animate-fade-in">
              <div className="flex items-center gap-3 mb-4">
                <img 
                  src="/lovable-uploads/917e51ff-8660-4c82-b344-b93189a34ba9.png" 
                  alt="GAMBGO Logo"
                  className="w-8 h-8"
                />
                <span className="text-xl font-bold">GAMBGO</span>
              </div>
              <p className="text-gray-400">
                Delivering happiness across Gambia. Your one-stop shop for food, groceries, and more.
              </p>
            </div>
            <div className="animate-fade-in" style={{ animationDelay: '0.1s' }}>
              <h3 className="font-semibold mb-4">Legal</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms" className="hover:text-white transition-colors">Terms & Conditions</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-gray-400">
            <p>&copy; 2026 GAMBGO. All rights reserved. Powered by gambgo express.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AboutUsPage;
